import { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { DollarSign, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  BENEFIT_CLASSIFICATIONS,
  buildAiCopqBreakdown,
  COPQ_BREAKDOWN_FIELDS,
  COPQ_INPUT_MODES,
  FINANCIAL_STATUSES,
  getProjectedImpactHealth,
  getProjectedImpactMetrics,
  hasCopqBreakdownValues,
  sumCopqBreakdown,
} from "./financialImpact";
import { DefineAiDisclosure } from "./DefineAiDisclosure";

export function FinancialTab({
  projectId,
  token,
  financialImpact,
  setFinancialImpact,
  calculatedFinancials,
  validationErrors,
  saving,
  aiGenerating,
  aiFinancialSuggestion,
  hasProblemText,
  saveFinancialImpact,
  generateFinancialSuggestion,
}) {
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  const updateFinancialField = (field, value) => {
    setFinancialImpact((prev) => {
      const next = { ...prev, [field]: value };
      return next;
    });
  };

  const updateCopqMode = (mode) => {
    setFinancialImpact((prev) => {
      if (mode === COPQ_INPUT_MODES.DETAILED) {
        const detailedTotal = String(sumCopqBreakdown(prev.copq_breakdown));
        return { ...prev, copq_input_mode: mode, copq_current: detailedTotal };
      }
      return { ...prev, copq_input_mode: mode };
    });
  };

  const updateCopqBreakdownField = (field, value) => {
    setFinancialImpact((prev) => {
      const nextBreakdown = {
        ...prev.copq_breakdown,
        [field]: value,
      };
      const detailedTotal = String(sumCopqBreakdown(nextBreakdown));
      return {
        ...prev,
        copq_breakdown: nextBreakdown,
        copq_current: detailedTotal,
      };
    });
  };

  const aiFormValues = useMemo(() => ({
    copq_input_mode: hasCopqBreakdownValues(buildAiCopqBreakdown(aiFinancialSuggestion))
      ? COPQ_INPUT_MODES.DETAILED
      : COPQ_INPUT_MODES.SIMPLE,
    financial_status: FINANCIAL_STATUSES.ESTIMATED,
    copq_breakdown: buildAiCopqBreakdown(aiFinancialSuggestion),
    copq_current: String(aiFinancialSuggestion?.total_copq_estimate || ""),
    copq_target: String(aiFinancialSuggestion?.suggested_target_copq || ""),
    implementation_cost: String(aiFinancialSuggestion?.implementation_cost_estimate || ""),
    benefit_classification: BENEFIT_CLASSIFICATIONS.ALL_HARD,
    hard_savings: "",
    soft_savings: "",
    notes: aiFinancialSuggestion?.rationale || "",
  }), [aiFinancialSuggestion]);

  const detailedCopqTotal = sumCopqBreakdown(financialImpact.copq_breakdown);

  const comparisonRows = [
    {
      key: "copq_current",
      label: "Current COPQ",
      current: financialImpact.copq_current,
      suggested: aiFormValues.copq_current,
    },
    {
      key: "copq_target",
      label: "Target COPQ",
      current: financialImpact.copq_target,
      suggested: aiFormValues.copq_target,
    },
    {
      key: "implementation_cost",
      label: "Implementation Cost",
      current: financialImpact.implementation_cost,
      suggested: aiFormValues.implementation_cost,
    },
  ];

  const formatCurrencyPreview = (value) => {
    if (value === "" || value == null) return "Not set";
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return `$${numericValue.toLocaleString()}`;
  };

  const confirmApplyAiValues = () => {
    setFinancialImpact(aiFormValues);
    setConfirmOverwriteOpen(false);
    console.info("financial_ai_overwrite_confirmed", {
      projectId,
      overwritten_fields: comparisonRows.map(({ key }) => key),
      current_values: {
        copq_current: financialImpact.copq_current,
        copq_target: financialImpact.copq_target,
        implementation_cost: financialImpact.implementation_cost,
      },
      ai_values: {
        copq_current: aiFormValues.copq_current,
        copq_target: aiFormValues.copq_target,
        implementation_cost: aiFormValues.implementation_cost,
      },
    });
    toast.info("AI estimates applied to form. Review and save.");
  };

  const projectedImpact = getProjectedImpactMetrics(calculatedFinancials);
  const impactHealth = getProjectedImpactHealth(projectedImpact);
  const healthStyles = {
    Strong: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
    Moderate: "bg-amber-500/15 text-amber-400 border-amber-500/25",
    Weak: "bg-red-500/15 text-red-400 border-red-500/25",
  };
  const financialStatus = projectedImpact?.financialStatus ?? financialImpact.financial_status ?? FINANCIAL_STATUSES.ESTIMATED;
  const financialStatusStyle = financialStatus === FINANCIAL_STATUSES.VALIDATED
    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
    : "border-amber-500/25 bg-amber-500/10 text-amber-400";
  const financialStatusLabel = financialStatus === FINANCIAL_STATUSES.VALIDATED ? "Validated" : "Estimated";

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading font-semibold">Financial Impact Estimator</h3>
            <p className="text-sm text-muted-foreground">
              Use Manual Mode for the standard deterministic calculator. AI Assist is optional and only suggests COPQ framing.
            </p>
          </div>
          <div className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${financialStatusStyle}`} data-testid="financial-status-badge">
            {financialStatusLabel}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-background/40 p-5 space-y-4" data-testid="manual-finance-mode">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">Manual Mode</p>
                <h4 className="mt-2 font-medium">Deterministic calculation</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter current and target COPQ plus implementation cost. Save &amp; Calculate computes the projected impact without AI.
                </p>
              </div>
              <div className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-emerald-400">
                Standard
              </div>
            </div>

            <div className="rounded-lg border border-border bg-background/50 p-3" data-testid="financial-status-help">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Current Status</p>
              <p className="mt-1 text-sm font-medium">{financialStatusLabel}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Define-phase financials default to Estimated. Update this status later when Measure-phase evidence validates the numbers.
              </p>
            </div>

            <div className="inline-flex rounded-lg border border-border bg-background/70 p-1" data-testid="copq-input-mode-toggle">
              <Button
                type="button"
                variant={financialImpact.copq_input_mode === COPQ_INPUT_MODES.SIMPLE ? "default" : "ghost"}
                size="sm"
                onClick={() => updateCopqMode(COPQ_INPUT_MODES.SIMPLE)}
                data-testid="copq-mode-simple-btn"
              >
                Simple total
              </Button>
              <Button
                type="button"
                variant={financialImpact.copq_input_mode === COPQ_INPUT_MODES.DETAILED ? "default" : "ghost"}
                size="sm"
                onClick={() => updateCopqMode(COPQ_INPUT_MODES.DETAILED)}
                data-testid="copq-mode-detailed-btn"
              >
                Detailed breakdown
              </Button>
            </div>

            {financialImpact.copq_input_mode === COPQ_INPUT_MODES.SIMPLE ? (
              <div className="space-y-2" data-testid="copq-simple-mode">
                <Label>Current Cost of Poor Quality (COPQ)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="e.g., 500000"
                    value={financialImpact.copq_current}
                    onChange={(e) => updateFinancialField("copq_current", e.target.value)}
                    className="pl-10"
                    data-testid="copq-current-input"
                  />
                </div>
                {validationErrors.copq_current && (
                  <p className="text-xs text-destructive" data-testid="copq-current-error">
                    {validationErrors.copq_current}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Enter a rolled-up annual COPQ total if you already know the full amount.
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="copq-detailed-mode">
                <div className="grid gap-3 sm:grid-cols-2">
                  {COPQ_BREAKDOWN_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label>{field.label}</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="0"
                          value={financialImpact.copq_breakdown[field.key]}
                          onChange={(e) => updateCopqBreakdownField(field.key, e.target.value)}
                          className="pl-10"
                          data-testid={`copq-breakdown-${field.key}-input`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-border bg-background/70 p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Current COPQ auto-sum</p>
                  <p className="mt-2 text-2xl font-bold font-heading" data-testid="copq-breakdown-total">
                    ${detailedCopqTotal.toLocaleString()}
                  </p>
                  {validationErrors.copq_current && (
                    <p className="mt-2 text-xs text-destructive" data-testid="copq-current-error">
                      {validationErrors.copq_current}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Detailed category entries roll up into Current COPQ automatically.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Target COPQ (After Improvement)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="e.g., 200000"
                  value={financialImpact.copq_target}
                  onChange={(e) => updateFinancialField("copq_target", e.target.value)}
                  className="pl-10"
                  data-testid="copq-target-input"
                />
              </div>
              {validationErrors.copq_target && (
                <p className="text-xs text-destructive" data-testid="copq-target-error">
                  {validationErrors.copq_target}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Implementation Cost</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="e.g., 50000"
                  value={financialImpact.implementation_cost}
                  onChange={(e) => updateFinancialField("implementation_cost", e.target.value)}
                  className="pl-10"
                  data-testid="implementation-cost-input"
                />
              </div>
              {validationErrors.implementation_cost && (
                <p className="text-xs text-destructive" data-testid="implementation-cost-error">
                  {validationErrors.implementation_cost}
                </p>
              )}
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-background/50 p-4" data-testid="benefit-classification-section">
              <div className="space-y-1">
                <Label>Benefit Classification</Label>
                <p className="text-xs text-muted-foreground">
                  Hard savings reduce budgeted spend or headcount. Soft savings improve capacity, service, or risk without immediately removing cost.
                </p>
              </div>

              <div className="inline-flex rounded-lg border border-border bg-background/70 p-1" data-testid="benefit-classification-toggle">
                <Button
                  type="button"
                  variant={financialImpact.benefit_classification === BENEFIT_CLASSIFICATIONS.ALL_HARD ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFinancialImpact((prev) => ({ ...prev, benefit_classification: BENEFIT_CLASSIFICATIONS.ALL_HARD, hard_savings: "", soft_savings: "" }))}
                  data-testid="benefit-classification-hard-btn"
                >
                  All hard
                </Button>
                <Button
                  type="button"
                  variant={financialImpact.benefit_classification === BENEFIT_CLASSIFICATIONS.MIXED ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFinancialImpact((prev) => ({ ...prev, benefit_classification: BENEFIT_CLASSIFICATIONS.MIXED }))}
                  data-testid="benefit-classification-mixed-btn"
                >
                  Mixed
                </Button>
                <Button
                  type="button"
                  variant={financialImpact.benefit_classification === BENEFIT_CLASSIFICATIONS.ALL_SOFT ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFinancialImpact((prev) => ({ ...prev, benefit_classification: BENEFIT_CLASSIFICATIONS.ALL_SOFT, hard_savings: "", soft_savings: "" }))}
                  data-testid="benefit-classification-soft-btn"
                >
                  All soft
                </Button>
              </div>

              {financialImpact.benefit_classification === BENEFIT_CLASSIFICATIONS.MIXED && (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="benefit-classification-mixed-inputs">
                  <div className="space-y-2">
                    <Label>Hard savings</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="0"
                        value={financialImpact.hard_savings}
                        onChange={(e) => updateFinancialField("hard_savings", e.target.value)}
                        className="pl-10"
                        data-testid="hard-savings-input"
                      />
                    </div>
                    {validationErrors.hard_savings && (
                      <p className="text-xs text-destructive" data-testid="hard-savings-error">
                        {validationErrors.hard_savings}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Soft savings</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="0"
                        value={financialImpact.soft_savings}
                        onChange={(e) => updateFinancialField("soft_savings", e.target.value)}
                        className="pl-10"
                        data-testid="soft-savings-input"
                      />
                    </div>
                    {validationErrors.soft_savings && (
                      <p className="text-xs text-destructive" data-testid="soft-savings-error">
                        {validationErrors.soft_savings}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional financial considerations..."
                value={financialImpact.notes}
                onChange={(e) =>
                  setFinancialImpact((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
                data-testid="financial-notes-input"
              />
            </div>
          </div>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-5 space-y-4" data-testid="ai-finance-mode">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">AI Assist Mode</p>
                <h4 className="mt-2 font-medium">Optional COPQ estimation support</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use AI only if you want a suggested COPQ breakdown and draft financial inputs. Manual calculation does not depend on this step.
                </p>
                <p className="mt-2 text-xs font-medium uppercase tracking-wider text-amber-400" data-testid="ai-financial-status-label">
                  AI outputs are always Estimated
                </p>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={generateFinancialSuggestion}
                disabled={!!aiGenerating || !hasProblemText}
                className="gap-2 shrink-0"
                data-testid="ai-suggest-financial-btn"
              >
                {aiGenerating === "Financial Estimate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Estimate COPQ with AI
                <CreditCostTag cost={5} />
              </Button>
            </div>

            {!hasProblemText && (
              <p className="text-xs text-muted-foreground" data-testid="ai-finance-hint">
                Add a problem statement first if you want AI-assisted COPQ suggestions.
              </p>
            )}

            {aiFinancialSuggestion && !aiFinancialSuggestion.ai_unavailable ? (
              <div className="space-y-3" data-testid="ai-financial-suggestion-panel">
                <DefineAiDisclosure
                  title="AI-generated estimate"
                  detail="These COPQ values are estimated from Define context. They may include assumptions, placeholder baselines, and suggested reductions that are not yet validated."
                  testId="ai-financial-disclosure"
                />
                <p className="text-xs font-medium text-primary uppercase tracking-wider">AI Suggested Estimate</p>
                <p className="text-sm">{aiFinancialSuggestion.rationale}</p>
                {aiFinancialSuggestion.copq_categories?.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between rounded border border-primary/15 bg-background p-2 text-sm">
                    <span>{cat.category}</span>
                    <span className="font-mono font-medium">${cat.estimated_annual_cost?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="grid gap-2 text-sm sm:grid-cols-3">
                  <div className="rounded border border-primary/15 bg-background p-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Suggested Current COPQ</p>
                    <p className="mt-1 font-semibold">${aiFinancialSuggestion.total_copq_estimate?.toLocaleString()}</p>
                  </div>
                  <div className="rounded border border-primary/15 bg-background p-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Suggested Target COPQ</p>
                    <p className="mt-1 font-semibold">${aiFinancialSuggestion.suggested_target_copq?.toLocaleString()}</p>
                  </div>
                  <div className="rounded border border-primary/15 bg-background p-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Suggested Implementation Cost</p>
                    <p className="mt-1 font-semibold">${aiFinancialSuggestion.implementation_cost_estimate?.toLocaleString()}</p>
                  </div>
                </div>
                {hasCopqBreakdownValues(aiFormValues.copq_breakdown) && (
                  <div className="rounded border border-primary/15 bg-background p-3" data-testid="ai-financial-breakdown-preview">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Mapped COPQ breakdown</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {COPQ_BREAKDOWN_FIELDS.map((field) => (
                        <div key={field.key} className="flex items-center justify-between text-sm">
                          <span>{field.label}</span>
                          <span className="font-medium">
                            {Number(aiFormValues.copq_breakdown[field.key] || 0) > 0
                              ? `$${Number(aiFormValues.copq_breakdown[field.key]).toLocaleString()}`
                              : "Not used"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {aiFinancialSuggestion.copq_categories?.some((cat) => cat?.basis) && (
                  <div className="rounded border border-primary/15 bg-background p-3" data-testid="ai-financial-assumptions">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Assumptions / estimation basis</p>
                    <div className="mt-2 space-y-2">
                      {aiFinancialSuggestion.copq_categories.map((cat, i) => (
                        cat?.basis ? (
                          <p key={`${cat.category}-${i}`} className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">{cat.category}:</span> {cat.basis}
                          </p>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}
                <Button
                  variant="outline" size="sm"
                  onClick={() => setConfirmOverwriteOpen(true)}
                  data-testid="apply-ai-financial-btn"
                >
                  Apply AI Suggestions to Manual Form
                </Button>
              </div>
            ) : (
              <div className="rounded border border-dashed border-primary/20 bg-background/50 p-4 text-sm text-muted-foreground" data-testid="ai-finance-placeholder">
                AI Assist is optional. Generate an estimate only if you want suggested COPQ categories and starting values.
              </div>
            )}
          </div>
        </div>

        {/* Calculated Results */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
            Projected Impact
          </h4>

          {projectedImpact ? (
            <div className="space-y-4" data-testid="projected-impact-panel">
              <div className="bg-background border border-border rounded-lg p-5 space-y-5" data-testid="projected-impact-summary-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Projected Impact</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Deterministic summary from saved COPQ and implementation inputs
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${financialStatusStyle}`}
                      data-testid="projected-impact-financial-status"
                    >
                      {financialStatusLabel}
                    </div>
                    <div
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${healthStyles[impactHealth]}`}
                      data-testid="projected-impact-health"
                    >
                      {impactHealth}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Annual Savings</p>
                    <p className="mt-2 text-2xl font-bold font-heading text-[hsl(var(--success))]" data-testid="gross-savings-value">
                      ${projectedImpact.annualSavings.toLocaleString()}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Net Benefit</p>
                    <p className={`mt-2 text-2xl font-bold font-heading ${projectedImpact.netBenefit >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid="net-benefit-value">
                      ${projectedImpact.netBenefit.toLocaleString()}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Hard Savings</p>
                    <p className="mt-2 text-2xl font-bold font-heading" data-testid="hard-savings-value">
                      ${Number(projectedImpact.hardSavings ?? 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Soft Savings</p>
                    <p className="mt-2 text-2xl font-bold font-heading" data-testid="soft-savings-value">
                      ${Number(projectedImpact.softSavings ?? 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4 sm:col-span-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Projected Benefit</p>
                    <p className="mt-2 text-3xl font-bold font-heading text-[hsl(var(--success))]" data-testid="total-projected-benefit-value">
                      ${Number(projectedImpact.totalProjectedBenefit ?? projectedImpact.annualSavings).toLocaleString()}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">ROI</p>
                    <p className="mt-2 text-2xl font-bold font-heading" data-testid="roi-value">
                      {projectedImpact.roiPercent != null ? `${projectedImpact.roiPercent.toFixed(1)}%` : "N/A"}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-card/60 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Payback Period</p>
                    <p className="mt-2 text-2xl font-bold font-heading" data-testid="payback-value">
                      {projectedImpact.paybackMonths != null ? `${projectedImpact.paybackMonths.toFixed(1)} mo` : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-background border border-border rounded-lg p-8 text-center" data-testid="projected-impact-placeholder">
              <DollarSign className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Enter COPQ values to calculate projected impact
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button onClick={saveFinancialImpact} disabled={saving} data-testid="save-financial-btn">
          {saving ? "Saving..." : "Save & Calculate"}
        </Button>
        <ArtifactQualityReview
          projectId={projectId}
          artifactType="financial_impact"
          token={token}
        />
      </div>

      <AlertDialog open={confirmOverwriteOpen} onOpenChange={setConfirmOverwriteOpen}>
        <AlertDialogContent data-testid="financial-ai-overwrite-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace current financial inputs?</AlertDialogTitle>
            <AlertDialogDescription>
              Applying the AI estimate will replace your current COPQ, target COPQ, and implementation cost values.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 rounded-lg border border-border bg-background/40 p-4 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Field</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">AI Suggestion</p>
            {comparisonRows.map((row) => (
              <FragmentRow
                key={row.key}
                label={row.label}
                currentValue={formatCurrencyPreview(row.current)}
                suggestedValue={formatCurrencyPreview(row.suggested)}
              />
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-ai-financial-overwrite-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmApplyAiValues} data-testid="confirm-ai-financial-overwrite-btn">
              Confirm Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FragmentRow({ label, currentValue, suggestedValue }) {
  return (
    <>
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-muted-foreground" data-testid={`current-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {currentValue}
      </p>
      <p className="font-medium text-foreground" data-testid={`suggested-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {suggestedValue}
      </p>
    </>
  );
}
